import { useTwttrLoad } from '@hooks/useTwitterEmbedPaste';
import {
  ContentsWrap,
  DetailContents,
} from '@pages/Posting/PostingDetail/sideType/DetailStyle';
import { replaceContents } from '@utils/replaceContents';
import React, { useCallback } from 'react';
import { sanitizeAndScopeHTML } from '@utils/cssScoping';
import { useNavigate } from 'react-router-dom';

type Props = {
  value: string | undefined;
  htmlValue?: string | undefined;
  isHtmlMode?: boolean;
  blockRightMouse?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  lineHeight?: number;
};
function QuillContents({
  value,
  htmlValue,
  isHtmlMode,
  blockRightMouse = false,
  style,
  onClick,
  lineHeight,
  ...props
}: Props) {
  useTwttrLoad(value || '');
  const navigate = useNavigate();

  // 내부 <a> 태그 클릭 시 같은 origin의 경로는 React Router로 처리 (SPA 이동)
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest('a');
      if (!target) {
        onClick?.();
        return;
      }
      const href = target.getAttribute('href');
      if (!href) return;

      // 새 탭 열기 속성이 있으면 브라우저 기본 동작 유지
      const targetAttr = target.getAttribute('target');
      if (targetAttr === '_blank') return;

      // 같은 origin의 내부 경로만 intercept
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin === window.location.origin) {
          e.preventDefault();
          navigate(url.pathname + url.search + url.hash);
        }
      } catch {
        // 유효하지 않은 URL이면 기본 동작 유지
      }
    },
    [navigate, onClick],
  );

  // 양쪽 소스에서 style 태그 여부 판단
  const hasStyleInHtml = !!(
    htmlValue && /<style[^>]*>[\s\S]*?<\/style>/i.test(htmlValue)
  );
  const hasStyleInValue = !!(
    value && /<style[^>]*>[\s\S]*?<\/style>/i.test(value)
  );

  // 스코프 처리된 HTML 생성
  const scopedHtmlFromHtmlValue = sanitizeAndScopeHTML(
    htmlValue || '',
    '.scoped-detail',
  );
  const scopedHtmlFromValue = sanitizeAndScopeHTML(
    value || '',
    '.scoped-detail',
  );

  // 래퍼 클래스: 어느 쪽이든 style 태그가 있으면 스코프 클래스 적용
  const wrapClassName =
    hasStyleInHtml || hasStyleInValue ? 'scoped-detail' : 'ql-editor';

  // 렌더링할 최종 HTML 결정
  const contentHTML = (() => {
    let html = '';
    if (isHtmlMode) html = scopedHtmlFromHtmlValue || '';
    else if (hasStyleInHtml) html = scopedHtmlFromHtmlValue || '';
    else if (hasStyleInValue) html = scopedHtmlFromValue || '';
    else html = value || htmlValue || '';

    // 기존 데이터 호환: 커스텀 이모지 내부의 <div>를 <span>으로 치환 (<p> 안에서 <div>는 불가)
    html = html.replace(
      /<div class="ql-custom-emoji-thumbnail"([^>]*)><\/div>/g,
      '<span class="ql-custom-emoji-thumbnail"$1></span>',
    );

    // 기존 데이터 호환: 인라인 스타일로 저장된 타임라인 번호 칩을 CSS 클래스 방식으로 변환
    html = html.replace(
      /<a([^>]*?)href="([^"]+)"([^>]*?)style="[^"]*background:\s*var\(--subColor\)[^"]*"([^>]*)>#(\d+)<\/a>/g,
      '<a href="$2" class="timeline-number-chip">#$5</a>',
    );

    return html;
  })();

  if (!contentHTML) return null;

  return (
    <ContentsWrap
      className={wrapClassName}
      blockMouse={blockRightMouse}
      style={style}
      onClick={handleClick}
      {...props}
    >
      <DetailContents
        dangerouslySetInnerHTML={{
          __html: contentHTML,
        }}
        style={{ lineHeight: lineHeight || 1.6 }}
      />
    </ContentsWrap>
  );
}

export default QuillContents;
